import { createCardBodySchema, type Card } from '@klopotun/contracts';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useSaveCardMutation } from '../../../../entities/card/index.js';
import { Button, TextField } from '../../../../shared/ui/index.js';
import styles from './new-card-panel.module.css';

type Complaints = {
  label?: string;
  last4?: string;
};

const COMPLAINT_BY_FIELD: Record<string, Complaints> = {
  label: { label: 'Вкажіть назву картки' },
  last4: { last4: 'Потрібні рівно чотири цифри' },
};

const complaintsOf = (paths: PropertyKey[][]): Complaints =>
  paths.reduce<Complaints>((complaints, path) => {
    const [firstSegment] = path;
    const complaint =
      typeof firstSegment === 'string' ? COMPLAINT_BY_FIELD[firstSegment] : undefined;

    return { ...complaints, ...complaint };
  }, {});

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
};

type NewCardPanelProps = {
  baseCurrency: string | null;
  onCreated: (card: Card) => void;
  onCancel: () => void;
};

/**
 * The shortest way from "which card?" to a card that exists: a name and, if wanted, the last four
 * digits, without leaving the payment form. Brand, expiry and colour wait on the cards screen.
 * Not a form element on purpose - it sits inside the payment form, and forms do not nest.
 */
export const NewCardPanel = ({ baseCurrency, onCreated, onCancel }: NewCardPanelProps) => {
  const [label, setLabel] = useState('');
  const [last4, setLast4] = useState('');
  const [complaints, setComplaints] = useState<Complaints>({});
  const labelRef = useRef<HTMLInputElement>(null);
  const saveCard = useSaveCardMutation();

  useEffect(() => {
    labelRef.current?.focus();
  }, []);

  const submit = (): void => {
    const checked = createCardBodySchema.safeParse({
      label: label.trim(),
      last4: emptyToNull(last4),
      brand: null,
      expiresMonth: null,
      expiresYear: null,
      currency: baseCurrency,
      color: null,
    });

    if (!checked.success) {
      setComplaints(complaintsOf(checked.error.issues.map((issue) => issue.path)));
      return;
    }

    setComplaints({});
    saveCard.mutate({ mode: 'create', body: checked.data }, { onSuccess: onCreated });
  };

  // Enter inside these inputs would otherwise submit the surrounding payment form.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    submit();
  };

  return (
    <div className={styles.panel} role="group" aria-label="Нова картка" onKeyDown={handleKeyDown}>
      <p className={styles.title}>Нова картка</p>

      <TextField
        label="Назва"
        name="newCardLabel"
        value={label}
        ref={labelRef}
        placeholder="Чорна зарплатна"
        error={complaints.label}
        onChange={(event) => {
          setLabel(event.target.value);
        }}
      />

      <TextField
        label="Останні 4 цифри"
        name="newCardLast4"
        value={last4}
        inputMode="numeric"
        placeholder="1234"
        hint="Не обов'язково"
        error={complaints.last4}
        onChange={(event) => {
          setLast4(event.target.value);
        }}
      />

      {saveCard.error !== null && (
        <p className={styles.actionError} role="alert">
          {saveCard.error.message}
        </p>
      )}

      <div className={styles.actions}>
        <Button busy={saveCard.isPending} onClick={submit}>
          Додати картку
        </Button>
        <Button variant="secondary" disabled={saveCard.isPending} onClick={onCancel}>
          Скасувати
        </Button>
      </div>
    </div>
  );
};
